import React from 'react';
import {Box, Button, TextField, Typography} from '@mui/material';
import {ApolloClient, LazyQueryExecFunction} from '@apollo/client';
import {
  GetBulkAccounts,
  GetBulkAccountsVariables,
} from '../../schema-types/GetBulkAccounts';
import {
  BulkAdminValues,
  useBulkFiltersList,
  clearFieldValue,
  getBulkAccountsVariablesFromValues,
} from './utils';
import {UPDATES} from '../../shared/UPDATES';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';

const isFilterInUse = (filter: string, values: BulkAdminValues): boolean => {
  switch (filter) {
    case 'nextReviewDateRange':
      return !!values.nextReviewDateFrom || !!values.nextReviewDateTo;
    case 'lastAtrDateRange':
      return !!values.lastAtrDateFrom || !!values.lastAtrDateTo;
    case 'cqUpdatedDateRange':
      return !!values.cqUpdatedDateFrom || !!values.cqUpdatedDateTo;
    case 'adviserEmails':
      return !!values.adviserEmails?.length;
    case 'ownerEmails':
      return !!values.ownerEmails?.length;
    case 'propositionTypes':
      return !!values.propositionTypes?.length;
    default:
      return false;
  }
};

export const BulkAdminPageHeader = ({
  loading,
  fetch,
  pageSize,
}: {
  pageSize: number;
  loading: boolean;
  fetch: LazyQueryExecFunction<GetBulkAccounts, GetBulkAccountsVariables>;
}) => {
  const {values, setFieldValue} = useAutoForm<BulkAdminValues>();
  const {search, selectedIDs} = values;
  const bulkFiltersList = useBulkFiltersList();
  return (
    <Box display="flex" justifyContent="space-between" alignItems="baseline">
      <Typography variant="h6">Filter by:</Typography>
      {bulkFiltersList.map((filter) => (
        <Button
          key={`${filter.value}`}
          color="primary"
          variant={isFilterInUse(`${filter?.value}`, values) ? 'contained' : 'outlined'}
          onClick={() => setFieldValue(UPDATES._1202, 'activeFilter', filter)}
          disabled={loading}
          data-test="bulk-admin-filter-btn"
        >
          {filter.label}
        </Button>
      ))}
      <Button
        color="secondary"
        variant="contained"
        onClick={async () => {
          // cleaning up formik from any filter data
          bulkFiltersList.forEach((x) => {
            clearFieldValue({setFieldValue, filter: x});
          });
          // then querying for the current selection minus filters

          fetch({
            variables: {
              ...getBulkAccountsVariablesFromValues(values),
              first: pageSize,
              after: null,
              propositionTypes: null,
              adviserEmails: null,
              ownerEmails: null,
              noAdviser: false,
              cqUpdatedDateRange: null,
              lastAtrDateRange: null,
              nextReviewDateRange: null,
            },
          });
        }}
        disabled={
          loading || !bulkFiltersList.some((x) => isFilterInUse(`${x.value}`, values))
        }
      >
        Remove all filters
      </Button>
      <Box flex={0.8} />
      <Box display="flex" alignItems="baseline">
        <TextField
          value={search}
          autoFocus
          onChange={async (e) => {
            const searchTerm = e.target.value;
            setFieldValue(UPDATES._1203, 'search', searchTerm);
            setFieldValue(UPDATES._1204, 'cursors', []);
            /**
             * Whoa! We're wiping the cache because Apollo graphQL loves to display some stale data
             */

            fetch({
              variables: {
                ...getBulkAccountsVariablesFromValues(values),
                first: pageSize,
                after: null,
                searchTerm,
              },
            });
          }}
          placeholder="Search clients"
          style={{minWidth: 400}}
          margin="normal"
          inputProps={{
            type: 'search',
          }}
          data-test="search-clients"
        />

        <Button
          style={{marginLeft: '1rem'}}
          color="primary"
          variant="contained"
          onClick={() => {
            setFieldValue(UPDATES._1205, 'isBulkActionsModalOpen', true);
          }}
          disabled={!selectedIDs.length}
        >
          Bulk actions
        </Button>
      </Box>
    </Box>
  );
};

export const getBulkAdminResultsCount = ({
  cursorsLength,
  pageSize,
  bulkAccountsLength,
  totalCount,
}: {
  cursorsLength: number;
  pageSize: number;
  bulkAccountsLength: number;
  totalCount: number;
}) => {
  const firstResult = pageSize * cursorsLength + 1;
  let lastResult = pageSize * (cursorsLength + 1);
  if (bulkAccountsLength < pageSize) {
    lastResult = bulkAccountsLength + pageSize * cursorsLength;
  }
  return `${firstResult} - ${lastResult} of ${totalCount}`;
};

export const BulkAdminPagePagination = ({
  loading,
  client,
  fetch,
  pageSize,
  hasNextPage,
  endCursor,
  startCursor,
  bulkAccountsLength,
  totalCount,
}: {
  endCursor: string | null | undefined;
  startCursor: string | null | undefined;
  hasNextPage?: boolean;
  pageSize: number;
  totalCount: number;
  bulkAccountsLength: number;
  loading: boolean;
  client: ApolloClient<any>;
  fetch: LazyQueryExecFunction<GetBulkAccounts, GetBulkAccountsVariables>;
}) => {
  const {values, setFieldValue} = useAutoForm<BulkAdminValues>();
  const {cursors} = values;
  return (
    <Box display="flex" justifyContent="end" alignItems="baseline">
      <Button
        color="primary"
        variant="contained"
        disabled={loading || cursors.length === 0}
        onClick={async () => {
          /**
           * Yikes! We're handling hasPreviousPage behaviour on our own because the library let us down :(
           */
          const lastCursor = cursors.slice(-1).pop();
          if (!lastCursor) {
            return;
          }
          const previousCursors = cursors.slice(0, -1);
          setFieldValue(UPDATES._1206, 'cursors', previousCursors);

          fetch({
            variables: {
              ...getBulkAccountsVariablesFromValues(values),
              first: pageSize,
              after: previousCursors.length > 0 ? lastCursor : null,
            },
          });
        }}
        data-test="previous"
      >
        Previous
      </Button>
      <Box ml={2}>
        <Typography>
          {getBulkAdminResultsCount({
            cursorsLength: cursors.length,
            pageSize,
            bulkAccountsLength,
            totalCount,
          })}
        </Typography>
      </Box>
      <Box ml={2}>
        <Button
          color="primary"
          variant="contained"
          disabled={!hasNextPage || loading}
          onClick={async () => {
            setFieldValue(UPDATES._1206, 'cursors', [...cursors, startCursor || '']);

            fetch({
              variables: {
                ...getBulkAccountsVariablesFromValues(values),
                first: pageSize,
                after: endCursor,
              },
            });
          }}
          data-test="next"
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};
